import { Combobox, FormDateRangePicker2, FormSelect, NoData } from '@/components';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from 'chart.js';
import React, { useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { Bar } from "react-chartjs-2";
import { UI_TEXT } from "./constants";
import useLabStatusTracker from './useLabStatusTracker';
import { useGetActiveGeoRegions } from '@/services/masterData/useGetActiveGeoRegions';
import { RegionSelect } from '@/components';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const LabStatusTracker: React.FC = () => {
  const {
    collectionsSummary,
    chartData,
    chartOptions,
    isLoading,
    error,
    dateRange,
    setDateRange,
    selectedValue,
    setSelectedValue,
    isDateRangeValid,
  } = useLabStatusTracker();

  // Create a form for filters so they can be submitted/cleared consistently with other pages
  const form = useForm({
    mode: "onChange",
    defaultValues: {
      dateRange: dateRange,
      region: selectedValue || "",
    },
  });

  const {
    data: geoRegionsData,
    isLoading: isLoadingGeoRegions,
    error: geoRegionsError,
  } = useGetActiveGeoRegions();

  const geographicalRegionOptions =
  geoRegionsData?.data?.map((region: any) => ({
    label: region.name,
    value: region.id,
  })) || [];

  // Sync form when selectedValue changes (handles clear all and programmatic changes)
  useEffect(() => {
    form.reset({
      dateRange: dateRange,
      region: selectedValue || "",
    }, { keepDirty: false, keepTouched: false });
  }, [selectedValue, dateRange, form]);


  if (isLoading) {
    return (
      <div className="w-full max-w-full mx-auto">
        <div className="bg-white rounded-lg shadow-md p-5 border border-gray-200">
          <h2 className="text-lg font-medium text-gray-800 mb-5">
            {UI_TEXT.TITLE}
          </h2>
          <div className="flex items-center justify-center py-8">
            <div className="text-gray-500">{UI_TEXT.LOADING}</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full max-w-full mx-auto">
        <div className="bg-white rounded-lg shadow-md p-5 border border-gray-200">
          <h2 className="text-lg font-medium text-gray-800 mb-5">
            {UI_TEXT.TITLE}
          </h2>
          <div className="flex items-center justify-center py-8">
            <div className="text-red-500">
              {UI_TEXT.ERROR_PREFIX} {error.message}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-full mx-auto">
      <div className="bg-white rounded-lg shadow-md p-5 border border-gray-200">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-5">
          {/* Title */}
          <h2 className="text-lg font-medium text-gray-800 mb-4 lg:mb-0">
            {UI_TEXT.TITLE}
          </h2>

          {/* Right-aligned filters */}
          <div className="flex flex-col sm:flex-row gap-3 lg:ml-auto lg:justify-end">
            {/* Use react-hook-form provider so these filters behave like other filters in app */}
            <FormProvider {...form}>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  
                  const formData = new FormData(e.currentTarget as HTMLFormElement);
                  const dr = formData.get("dateRange") as any;
                  const reg = formData.get("region") as any;

                  
                  if (dr) {
                    try {
                      // dateRange will be an object in components; attempt JSON parse if string
                      const parsed = typeof dr === "string" ? JSON.parse(dr) : dr;
                      setDateRange(parsed);
                    } catch {
                      // ignore parse errors
                    }
                  }
                  if (reg) {
                    setSelectedValue(String(reg));
                  }
                }}
                className="flex w-full gap-3"
              >
                {/* Date Range Picker */}
                <div className="w-full sm:w-56">
                  <FormDateRangePicker2
                    name="dateRange"
                    control={form.control}
                    value={dateRange}
                    onChange={(range: any) => {
                      form.setValue("dateRange", range);
                      setDateRange(range);
                    }}
                  />
                  {/* Show validation error */}
                  {!isDateRangeValid && (
                    <p className="text-red-500 text-xs mt-1">
                      {"End date must be after start date"}
                    </p>
                  )}
                </div>

                {/* Region Dropdown */}
                <div className="w-full sm:w-48">
                  <RegionSelect
                    name="region"
                    control={form.control}
                    options={geographicalRegionOptions}
                    onChange={(val: any) => {
                      setSelectedValue(val || "");
                    }}
                    placeholder="Select Region"
                    isLoading={isLoadingGeoRegions}
                  />
                </div>

                {/* Clear All link */}
                <div className="flex items-center sm:items-start">
                  <button
                    type="button"
                    onClick={() => {
                      // Reset all states and form values
                      setDateRange({ from: undefined, to: undefined });
                      setSelectedValue("");
                    }}

                    className="text-sm cursor-pointer underline text-gray-600 hover:text-gray-800"
                  >
                    Clear All
                  </button>
                </div>

                
                <input type="submit" className="hidden" />
              </form>
            </FormProvider>
          </div>
        </div>

        {/* Chart Container */}
        <div className="h-100">
          {!isDateRangeValid ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-amber-500 text-center">
                <p className="font-medium">{ "Invalid Date Range"}</p>
                <p className="text-sm mt-1">
                  {"Please ensure the end date is after the start date."}
                </p>
              </div>
            </div>
          ) : collectionsSummary.length === 0 ? (
            <NoData
              title={UI_TEXT.NO_DATA_TITLE}
              description={UI_TEXT.NO_DATA_DESCRIPTION}
            />
          ) : (
            <Bar data={chartData} options={chartOptions} />
          )}
        </div>
      </div>
    </div>
  );
};

export default LabStatusTracker;